import { CATEGORY_TYPES, KNOWLEDGE_RESOURCES_TYPES, KNOWLEDGE_TYPES } from '../constants';
import { INTERNAL_ROUTING, ROUTES } from '../constants/routes.js';
import { isVideoUrl } from './index';
import _ from 'lodash';
import { useKnowledgeItemsById } from '../hooks/knowledge-items.hooks';

const KNOWLEDGE_ITEM_LINK = `${ROUTES.KNOWLEDGE}/items/{id}`;
const KNOWLEDGE_CATEGORY_LINK = `${ROUTES.KNOWLEDGE}/{id}`;
const KNOWLEDGE_BINDER_LINK = `${ROUTES.KNOWLEDGE}?${INTERNAL_ROUTING.QUERY_PARAMS.KNOWLEDGE}={id}`;
const LABS_CATEGORY_LINK = `${ROUTES.LABS}/{id}`;
const LABS_BINDER_LINK = `${ROUTES.LABS}?${INTERNAL_ROUTING.QUERY_PARAMS.COMPENDIUM}={id}`;
const ADMISSION_CATEGORY_LINK = `${ROUTES.ADMISSION}/{id}`;
const ADMISSION_BINDER_LINK = `${ROUTES.ADMISSION}?${INTERNAL_ROUTING.QUERY_PARAMS.CHECKLIST}={id}`;

const ALL_CATEGORY_TYPES = [
  CATEGORY_TYPES.CHECKS,
  CATEGORY_TYPES.VALUES,
  CATEGORY_TYPES.KNOWLEDGE,
  CATEGORY_TYPES.CATEGORY,
];

const CATEGORY_TYPE_TO_LINK = {
  [KNOWLEDGE_RESOURCES_TYPES.KNOWLEDGE_CATEGORY]: {
    get: (item, store) => {
      if (!item) {
        return null;
      }

      const { id, title } = item;
      const category = store?.knowledge?.categoriesById?.[id] || {};

      return {
        id,
        ...category,
        type: category?.type || CATEGORY_TYPES.CATEGORY,
        title,
        navigateTo:
          category?.type === CATEGORY_TYPES.CATEGORY
            ? KNOWLEDGE_CATEGORY_LINK.replace('{id}', id)
            : KNOWLEDGE_BINDER_LINK.replace('{id}', id),
      };
    },
  },
  [KNOWLEDGE_RESOURCES_TYPES.KNOWLEDGE_ITEM]: {
    get: (item, store) => {
      if (!item) {
        return null;
      }
      const { id, title } = item;
      const MAPPER = {
        [KNOWLEDGE_TYPES.PDF]: KNOWLEDGE_TYPES.CONTENT,
        [KNOWLEDGE_TYPES.DOCUMENT]: KNOWLEDGE_TYPES.CONTENT,
        [KNOWLEDGE_TYPES.CONTENT]: KNOWLEDGE_TYPES.CONTENT,
      };
      const knowledgeItem = store?.knowledgeItemsById?.[id] || {};

      const isVideo =
        knowledgeItem?.type === KNOWLEDGE_TYPES.VIDEO || isVideoUrl(knowledgeItem?.link);
      const type = isVideo
        ? KNOWLEDGE_TYPES.VIDEO
        : MAPPER[knowledgeItem?.type] || KNOWLEDGE_TYPES.CONTENT;

      return {
        id,
        ...knowledgeItem,
        title,
        type,
        navigateTo: KNOWLEDGE_ITEM_LINK.replace('{id}', id),
      };
    },
  },
  [KNOWLEDGE_RESOURCES_TYPES.CHECKLIST]: {
    get: (item, store) => {
      if (!item) {
        return null;
      }

      const { id, title } = item;
      const checklist = store?.admission?.categoriesById?.[id] || {};

      return {
        id,
        ...checklist,
        title,
        type: checklist?.type || CATEGORY_TYPES.CHECKS,
        categoryType: CATEGORY_TYPES.CHECKS,
        navigateTo:
          checklist?.type === CATEGORY_TYPES.CATEGORY
            ? ADMISSION_CATEGORY_LINK.replace('{id}', id)
            : ADMISSION_BINDER_LINK.replace('{id}', id),
      };
    },
  },
  [KNOWLEDGE_RESOURCES_TYPES.LAB]: {
    get: (item, store) => {
      if (!item) {
        return null;
      }

      const { id, title } = item;
      const compendium = store?.lab?.categoriesById?.[id] || {};

      return {
        id,
        ...compendium,
        title,
        type: compendium?.type || CATEGORY_TYPES.VALUES,
        categoryType: CATEGORY_TYPES.VALUES,
        navigateTo:
          compendium?.type === CATEGORY_TYPES.CATEGORY
            ? LABS_CATEGORY_LINK.replace('{id}', id)
            : LABS_BINDER_LINK.replace('{id}', id),
      };
    },
  },
  [KNOWLEDGE_RESOURCES_TYPES.EXTERNAL_URL]: {
    get: item => ({ ...item, title: item.title || item.url, navigateTo: item.url }),
  },
  [KNOWLEDGE_RESOURCES_TYPES.FILE]: {
    get: item => ({ ...item, title: item.title || item.url, navigateTo: item.url }),
  },
};

const parse = (item, store) => {
  const mapper = CATEGORY_TYPE_TO_LINK[item?.type];
  return mapper?.get(item, store);
};

const createKnowledgePayload = item => {
  if (item.url) {
    return {
      type: KNOWLEDGE_RESOURCES_TYPES.EXTERNAL_URL,
      ...item,
    };
  }

  let type = KNOWLEDGE_RESOURCES_TYPES.KNOWLEDGE_ITEM;
  if (ALL_CATEGORY_TYPES.includes(item?.type)) {
    if (item.values) {
      type = KNOWLEDGE_RESOURCES_TYPES.LAB;
    } else if (item.checks) {
      type = KNOWLEDGE_RESOURCES_TYPES.CHECKLIST;
    } else {
      type = KNOWLEDGE_RESOURCES_TYPES.KNOWLEDGE_CATEGORY;
    }
  }

  if (item?.type === CATEGORY_TYPES.VALUES) {
    type = KNOWLEDGE_RESOURCES_TYPES.LAB;
  } else if (item?.type === CATEGORY_TYPES.CHECKS) {
    type = KNOWLEDGE_RESOURCES_TYPES.CHECKLIST;
  }

  return {
    type,
    id: item.id,
    title: item.title,
  };
};

/**
 * The knowledge item ids inside a resource list, for the row fetch. The API sends
 * `null` for an empty list, not `[]`.
 *
 * @param {object[]|null|undefined} resources - Resources as saved on the entity.
 * @returns {string[]} Ids of the `KNOWLEDGE_ITEM` resources.
 * @example
 * useKnowledgeItemsById(toKnowledgeItemIds(rotation.knowledge));
 */
export const toKnowledgeItemIds = resources =>
  (resources ?? [])
    .filter(resource => resource?.type === KNOWLEDGE_RESOURCES_TYPES.KNOWLEDGE_ITEM)
    .map(resource => resource.id);

/**
 * Resolves a resource list (rotation / communication / event / quality-metric /
 * profile attachments) into navigable rows. Categories, checklists and labs come
 * from the store slices; knowledge item rows are fetched by id, so pass the
 * `resources` the component will `parse` — a row parsed before its fetch lands
 * carries only the id and title it was saved with.
 *
 * @param {object} params
 * @param {*} params.knowledge - The knowledge slice (categories).
 * @param {*} params.admission - The admission slice (checklists).
 * @param {*} params.lab - The lab slice (compendiums).
 * @param {object[]|null} [params.resources] - The resources about to be parsed; their knowledge item ids are fetched.
 * @returns {{ parse: (resource: object) => object|null, compose: (item: object) => object, isLoading: boolean }}
 *   `parse` turns a saved resource into a row with `navigateTo`; `compose` turns a picked item into a
 *   resource to save; `isLoading` is true while the knowledge item rows are still being fetched, when
 *   a parsed knowledge row carries only its saved id and title.
 * @example
 * const { parse } = useKnowledgeParser({ knowledge, admission, lab, resources: rotation.knowledge });
 * rotation.knowledge.map(parse);
 */
export const useKnowledgeParser = ({ knowledge, admission, lab, resources }) => {
  const { itemsById: knowledgeItemsById, isLoading } = useKnowledgeItemsById(
    toKnowledgeItemIds(resources),
  );

  return {
    parse: item => parse(item, { knowledge, admission, lab, knowledgeItemsById }),
    compose: item => createKnowledgePayload(item),
    isLoading,
  };
};

/**
 * General function to match user properties with schedule configuration
 * @param {Object} user - User object with professional properties
 * @param {Object} scheduleConfig - Schedule configuration object
 * @param {Array} priorityFields - Array of field names in priority order
 * @returns {Object} - { matches: boolean, matchedFields: [], priority: number }
 */
export const matchUserWithSchedule = (user, scheduleConfig, priorityFields = []) => {
  const userProfessional = { ...(user.professional || {}), profession: user.profession || '' };
  const matchedFields = [];

  // Check each field in the schedule configuration
  Object.keys(scheduleConfig || {}).forEach(field => {
    const scheduleValues = scheduleConfig[field];
    const userValue = userProfessional[field];

    // Skip if schedule doesn't have this field or it's empty
    if (!scheduleValues || (Array.isArray(scheduleValues) && scheduleValues.length === 0)) {
      return;
    }

    // Normalize both to arrays for comparison
    const scheduleArray = Array.isArray(scheduleValues) ? scheduleValues : [scheduleValues];
    const userArray = Array.isArray(userValue) ? userValue : [userValue];

    // Check if there's any intersection between user values and schedule values
    const hasMatch = _.intersection(scheduleArray, userArray).length > 0;

    if (hasMatch) {
      matchedFields.push(field);
    }
  });

  // Calculate priority based on matched fields
  let priority = Infinity; // Default to lowest priority

  if (matchedFields.length > 0) {
    // Find the highest priority (lowest index) among matched fields
    priority = Math.min(
      ...matchedFields.map(field => {
        const index = priorityFields.indexOf(field);
        return index === -1 ? Infinity : index;
      }),
    );
  }

  return {
    matches: matchedFields.length > 0,
    matchedFields,
    priority,
    matchCount: matchedFields.length,
  };
};

/**
 * Sort schedules by user match priority
 * @param {Array} rotationsBySchedule - Array of schedule objects
 * @param {Object} user - User object
 * @param {Array} priorityFields - Priority order for matching
 * @returns {Array} - Sorted array with priority information
 */
export const sortSchedulesByUserPriority = (
  rotationsBySchedule,
  user,
  priorityFields = ['department', 'division'],
) => {
  return rotationsBySchedule
    .map(schedule => {
      const matchResult = matchUserWithSchedule(user, schedule.configuration || {}, priorityFields);

      return {
        ...schedule,
        userMatch: matchResult,
      };
    })
    .sort((a, b) => {
      // Sort by priority (lower number = higher priority)
      if (a.userMatch.priority !== b.userMatch.priority) {
        return a.userMatch.priority - b.userMatch.priority;
      }

      // If same priority, sort by number of matches (more matches first)
      if (a.userMatch.matchCount !== b.userMatch.matchCount) {
        return b.userMatch.matchCount - a.userMatch.matchCount;
      }

      // Finally, sort alphabetically by name
      return a.name.localeCompare(b.name);
    });
};

/**
 * Find the best matching schedule for a user
 * @param {Array} rotationsBySchedule - Array of schedule objects
 * @param {Object} user - User object
 * @param {Array} priorityFields - Priority order for matching
 * @returns {Object|null} - Best matching schedule or null
 */
export const findBestMatchingSchedule = (
  rotationsBySchedule,
  user,
  priorityFields = ['department', 'division'],
) => {
  const sortedSchedules = sortSchedulesByUserPriority(rotationsBySchedule, user, priorityFields);
  const bestMatch = sortedSchedules.find(schedule => schedule.userMatch.matches);

  return bestMatch || null;
};
